import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { validateExam, type EvaluationBlock, type EvaluationExam } from '@interactive-room';
import { deliveryProfileFromExam } from '../studio/constants/studioMaterialRoutes';
import { getEvaluationExam, saveEvaluationExam } from '../studio/examStorage';
import { SUBJECT_CATALOG } from '../studio/constants/subjectCatalog';
import { PrintWorksheetDocument } from '../studio/print/PrintWorksheetDocument';
import { PrintMaterialChatPanel } from '../studio/print/PrintMaterialChatPanel';
import { GenerationLoadingOverlay } from '../components/ui/GenerationLoadingOverlay';
import { StaggerReveal, STAGGER_TOOLBAR_BASE_DELAY_MS, getStaggerDelayMs } from '../components/ui/StaggerReveal';
import { getCourseProfileById } from '../studio/services/courseProfileStorage';
import { resolveExamAiLevel } from '../studio/lib/resolveExamAiIdentity';
import { buildCourseDashboardPath } from '../studio/constants/courseStudioRoutes';
import type { ExamLevelId } from '../studio/constants/examAiParams';

function resolveSubject(subjectTag: string) {
  return SUBJECT_CATALOG.find((s) => s.tag === subjectTag || s.id === subjectTag);
}

type PrintLocationState = {
  generatedNotice?: string;
  fromGeneration?: boolean;
};

export function StudioPrintPage() {
  const { examId: paramExamId } = useParams<{ examId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const locationState = (location.state as PrintLocationState | null) ?? null;
  const generatedNotice = locationState?.generatedNotice;
  const fromGeneration = Boolean(locationState?.fromGeneration);
  const examId = paramExamId ?? searchParams.get('exam') ?? '';
  const editMode = searchParams.get('edit') === '1' || fromGeneration;

  const [exam, setExam] = useState<EvaluationExam | null>(() =>
    examId ? getEvaluationExam(examId) : null,
  );
  const [saveFlash, setSaveFlash] = useState<string | null>(null);

  useEffect(() => {
    setExam(examId ? getEvaluationExam(examId) : null);
  }, [examId]);

  // Tras generar: entrar directo en modo estudio (chat + hoja).
  useEffect(() => {
    if (!fromGeneration || searchParams.get('edit') === '1') return;
    const params = new URLSearchParams(searchParams);
    params.set('edit', '1');
    setSearchParams(params, { replace: true });
    // Solo al aterrizar desde generación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromGeneration]);

  const subject = exam ? resolveSubject(exam.subjectTag) : undefined;
  const linkedCourseId = exam?.courseId || exam?.linkedSession?.courseId;
  const course = linkedCourseId ? getCourseProfileById(linkedCourseId) : undefined;
  const schoolName = course?.schoolName;
  const resolvedLevel = useMemo(
    () => resolveExamAiLevel(exam, course?.level),
    [exam, course?.level],
  );

  const [overlayVisible, setOverlayVisible] = useState(fromGeneration);

  useEffect(() => {
    if (!fromGeneration) return;
    const frameId = window.requestAnimationFrame(() => {
      setOverlayVisible(false);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [fromGeneration]);

  useEffect(() => {
    if (searchParams.get('auto') === '1' && exam && !editMode) {
      const timer = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timer);
    }
  }, [exam, searchParams, editMode]);

  const setEditMode = useCallback(
    (next: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('edit', '1');
      else params.delete('edit');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const persistExam = useCallback((next: EvaluationExam, flash?: string) => {
    const saved = saveEvaluationExam(validateExam(next));
    setExam(saved);
    if (flash) {
      setSaveFlash(flash);
      window.setTimeout(() => setSaveFlash(null), 1800);
    }
    return saved;
  }, []);

  const handleBlockChange = useCallback(
    (nextBlock: EvaluationBlock) => {
      if (!exam) return;
      const blocks = exam.blocks.map((block) =>
        block.id === nextBlock.id ? { ...nextBlock } : block,
      );
      persistExam({ ...exam, blocks }, 'Bloque guardado');
    },
    [exam, persistExam],
  );

  const handleExamRevised = useCallback(
    (next: EvaluationExam) => {
      persistExam(next, 'Cambios de IA aplicados');
    },
    [persistExam],
  );

  const handleInsertBlock = useCallback(
    (block: EvaluationBlock, insertAt: number) => {
      if (!exam) return;
      const nextBlocks = [...exam.blocks];
      const at = Math.max(0, Math.min(insertAt, nextBlocks.length));
      nextBlocks.splice(at, 0, block);
      persistExam(
        {
          ...exam,
          blocks: nextBlocks.map((b, i) => ({ ...b, index: i })),
        },
        'Bloque insertado',
      );
    },
    [exam, persistExam],
  );

  const handleLevelResolved = useCallback(
    (level: ExamLevelId) => {
      if (!exam) return;
      persistExam({ ...exam, level }, 'Nivel guardado');
    },
    [exam, persistExam],
  );

  // Si el curso tiene nivel y el material no, persistirlo (evita 400 en generate-inline).
  useEffect(() => {
    if (!exam || exam.level || !course?.level) return;
    const level = resolveExamAiLevel(exam, course.level);
    if (!level) return;
    persistExam({ ...exam, level });
  }, [exam, course?.level, persistExam]);

  if (!exam || exam.blocks.length === 0) {
    return (
      <div className="print-page-shell">
        <div className="print-page-toolbar no-print">
          <Link to="/studio" className="print-toolbar-btn">
            ← Studio
          </Link>
        </div>
        <div className="print-page-empty">
          <p>No se encontró la evaluación o no tiene bloques para imprimir.</p>
          <Link to="/studio" className="print-toolbar-btn print-toolbar-btn--primary">
            Volver al Studio
          </Link>
        </div>
      </div>
    );
  }

  const backHref = linkedCourseId
    ? buildCourseDashboardPath(linkedCourseId)
    : subject
      ? `/studio/asignatura/${subject.id}`
      : '/studio';
  const isGuide = deliveryProfileFromExam(exam) === 'learning_guide' || exam.resourceType === 'guide';
  const toolbarRevealDelayMs = STAGGER_TOOLBAR_BASE_DELAY_MS + getStaggerDelayMs(exam.blocks.length);

  const toolbar = (
    <>
      <Link to={backHref} className="print-toolbar-btn">
        ← Volver
      </Link>
      <div className="print-page-toolbar__actions">
        {editMode ? (
          <button type="button" className="print-toolbar-btn print-toolbar-btn--active" onClick={() => setEditMode(false)}>
            Vista previa
          </button>
        ) : (
          <button type="button" className="print-toolbar-btn print-toolbar-btn--primary" onClick={() => setEditMode(true)}>
            {isGuide ? 'Editar guía' : 'Editar'}
          </button>
        )}
        <button type="button" className="print-toolbar-btn" onClick={() => window.print()}>
          🖨️ Imprimir / Guardar PDF
        </button>
      </div>
    </>
  );

  const worksheet = (
    <PrintWorksheetDocument
      exam={exam}
      subjectName={subject?.name ?? exam.subjectTag}
      subjectTag={subject?.tag ?? exam.subjectTag}
      revealStagger={fromGeneration && !editMode}
      schoolName={schoolName}
      editable={editMode}
      onBlockChange={handleBlockChange}
      onInsertBlock={editMode ? handleInsertBlock : undefined}
    />
  );

  return (
    <div className={`print-page-shell${editMode ? ' print-page-shell--editing print-page-shell--studio' : ''}`}>
      <GenerationLoadingOverlay visible={overlayVisible} />

      {fromGeneration ? (
        <StaggerReveal
          enabled
          index={0}
          variant="down"
          className="print-page-toolbar no-print"
          style={{ animationDelay: `${toolbarRevealDelayMs}ms` }}
        >
          {toolbar}
        </StaggerReveal>
      ) : (
        <div className="print-page-toolbar no-print">{toolbar}</div>
      )}

      {editMode ? (
        <div className="print-edit-banner no-print" role="status">
          <p>
            <strong>Estudio.</strong> Chat a la izquierda · hoja a la derecha. Entre bloques aparece{' '}
            <em>+ Bloque</em> para insertar antes o después. Pulsá <em>Editar</em> en un bloque para
            corregir a mano.
          </p>
          {saveFlash ? <span className="print-edit-banner__flash">{saveFlash}</span> : null}
        </div>
      ) : null}

      {generatedNotice && (
        <div className="no-print mx-auto mb-4 max-w-3xl rounded-lg border border-emerald-200/80 bg-emerald-50/90 px-4 py-2.5 text-sm text-emerald-800">
          {generatedNotice}
        </div>
      )}

      {editMode ? (
        <div className="print-studio-layout no-print-parent">
          <PrintMaterialChatPanel
            exam={exam}
            subjectExpertPrompt={subject?.aiExpertPrompt}
            level={resolvedLevel}
            onExamRevised={handleExamRevised}
            onLevelResolved={handleLevelResolved}
          />
          <div className="print-studio-canvas">{worksheet}</div>
        </div>
      ) : (
        worksheet
      )}
    </div>
  );
}
